import { pricingSuffix, type CohortUser, type PricingSuffix } from "~/utils/pricing"

// Shape of a pricing card in the hardcoded plan lists below. Named so packagesV2 and packagesV3
// declare their empty `yearly` arrays identically instead of repeating the literal twice.
export type PricingPackage = {
  title: string
  tag: string
  pkgRentals: number
  pkgDesc: string
  monthlyPrice: number
  yearlyPrice?: number
  period: string
  description: string
  actionLabel: string
  popular?: boolean
  features: string[]
}

export const packagesV2 = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie-v2",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals",
      monthlyPrice: 20,
      period: "month",
      description: "Want a few videos a month? This is the perfect plan to explore at your own pace.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "5 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker-v2",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals",
      monthlyPrice: 60,
      period: "month",
      description: "Already sold? Start making MLNP a part of your daily life with a new video rental every day.",
      actionLabel: "Subscribe Now",
      popular: true,
      features: [
        "Access to thousands of intro videos",
        "30 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge-v2",
      pkgRentals: 60,
      pkgDesc: "includes 60 video rentals",
      monthlyPrice: 100,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "60 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [] as PricingPackage[],
}

// v3 is v2 with exactly one price change: Quickie $20 -> $15. Every other field — copy, credit
// counts, feature lists, Daymaker $60, Binge $100 — is identical to packagesV2 by design. None of
// the v2 copy strings embed a price, which is what makes this a pure tag + price diff.
export const packagesV3 = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie-v3",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals",
      monthlyPrice: 15,
      period: "month",
      description: "Want a few videos a month? This is the perfect plan to explore at your own pace.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "5 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker-v3",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals",
      monthlyPrice: 60,
      period: "month",
      description: "Already sold? Start making MLNP a part of your daily life with a new video rental every day.",
      actionLabel: "Subscribe Now",
      popular: true,
      features: [
        "Access to thousands of intro videos",
        "30 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge-v3",
      pkgRentals: 60,
      pkgDesc: "includes 60 video rentals",
      monthlyPrice: 100,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "60 credits that never expire while subscribed",
        "Credits bank — unused credits carry over every month",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [] as PricingPackage[],
}

export const packages = {
  monthly: [
    {
      title: "Quickie",
      tag: "quickie",
      pkgRentals: 5,
      pkgDesc: "includes 5 video rentals per month",
      monthlyPrice: 10,
      period: "month",
      description:
        "Want a few videos a month for a better price? This is the perfect plan to explore at your own pace. There’s a lot of #RealWorldSex to enjoy!",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 5 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Daymaker",
      tag: "daymaker",
      pkgRentals: 30,
      pkgDesc: "includes 30 video rentals per month",
      monthlyPrice: 30,
      period: "month",
      description:
        "Already sold? Start making MLNP a part of your daily life with a new video rental every day. Isn’t this more fun than Netflix?",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 30 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Binge",
      tag: "binge",
      pkgRentals: 50,
      pkgDesc: "includes 50 video rentals per month",
      monthlyPrice: 50,
      period: "month",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 50 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
  yearly: [
    {
      title: "Annual Quickie",
      tag: "quickie-annual",
      pkgDesc: "includes 5 videos per month",
      monthlyPrice: 10,
      yearlyPrice: 100,
      period: "year",
      description:
        "Want a few videos a month for a better price? This is the perfect plan to explore at your own pace. There’s a lot of #RealWorldSex to enjoy!",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 5 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Annual Daymaker",
      tag: "daymaker-annual",
      pkgDesc: "includes 30 videos per month",
      monthlyPrice: 30,
      yearlyPrice: 300,
      period: "year",
      description:
        "Already sold? Start making MLNP a part of your daily life with a new video rental every day. Isn’t this more fun than Netflix?",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 30 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
    {
      title: "Annual Binge",
      tag: "binge-annual",
      pkgDesc: "includes 50 videos per month",
      monthlyPrice: 50,
      yearlyPrice: 500,
      period: "year",
      description: "Get come, get cozy and binge on #RealWorldSex! Watch a whole bunch of videos anytime you want.",
      actionLabel: "Subscribe Now",
      features: [
        "Access to thousands of intro videos",
        "Unlimited viewing of 50 rentals for 4 weeks",
        "Weekly MLNP curated and new video uploads email love letters",
        "Dedicated daily video recommendation email love letter from Cindy",
        "Stream on your favorite devices",
        "Priority access to new videos and curated playlists",
        "Subscription credits renew in 30 days",
        "Switch plans, cancel or upgrade anytime",
      ],
    },
  ],
}

// The plan list a viewer's pricing suffix selects. Declared after `packages` because these are
// `const` bindings — referencing `packages` above its declaration would throw at module load.
//
// Keyed by PricingSuffix so adding a generation is one line here plus a new packagesVN above, and
// so no call site can reintroduce the `isV2 ? packagesV2 : packages` bug that silently served a
// v3 viewer the LEGACY prices. Index it with pricingSuffix(user), never with a boolean.
export const packagesBySuffix: Record<PricingSuffix, { monthly: PricingPackage[]; yearly: PricingPackage[] }> = {
  "": packages,
  "-v2": packagesV2,
  "-v3": packagesV3,
}

// The "from $X/month" a viewer is quoted: the cheapest monthly plan in their own generation.
export const lowestMonthlyPrice = (user: CohortUser | unknown) =>
  Math.min(...packagesBySuffix[pricingSuffix(user)].monthly.map((pkg) => pkg.monthlyPrice))
